<template>
  <div>
    <div class="tw-bg-white tw-drop-shadow-xl tw-rounded-b-lg tw-p-3">
      <div class="tw-flex tw-justify-between tw-items-center">
        <div class="tw-font-bold tw-tracking-wider tw-text-gray-500 tw-my-3">
          <div class="tw-text-sm">Case ID: {{ caseDetail.id }} <br /></div>
          <div v-if="caseDetail.location" class="tw-text-lg">
            {{ caseDetail.location.address.street }} {{ caseDetail.location.address.house_number }}
          </div>
        </div>

        <CaseStatus :status="caseDetail.status" />
      </div>
      <div class="tw-flex tw-mt-2">
        <div class="tw-flex tw-justify-between tw-items-center tw-w-full" v-if="caseDetail.alarm">
          <div>
            <span class="tw-font-bold tw-mr-2">Alarm</span>
            <span>{{ caseDetail.alarm.type }}</span>
          </div>
          <AlarmStatus :alarm="caseDetail.alarm" v-if="caseDetail.alarm" />
        </div>
        <div v-else>
          <span class="tw-font-bold tw-mr-2">This case was not created from alarm</span>
        </div>
      </div>
      <div class="tw-flex tw-mt-2">
        <span class="tw-font-bold tw-mr-2">Category</span>
        <span>{{ caseDetail.category }}</span>
        <button
          class="tw-bg-gray-600 hover:tw-bg-gray-400 tw-rounded tw-p-1 tw-text-white tw-w-1/2 tw-ml-5"
          @click="showConfirmation('updateCategory')"
        >
          Update Category
        </button>
      </div>
      <div class="tw-flex tw-mt-2">
        <span class="tw-font-bold tw-mr-2">Monitoring</span>
        <MonitoringLinks :location-id="caseDetail.location_id" />
      </div>
      <div class="tw-flex tw-mt-2">
        <span class="tw-font-bold tw-mr-2">Woco</span>
        <span>{{ caseDetail.woco_name }}</span>
      </div>
      <div class="tw-flex tw-mt-2">
        <span class="tw-font-bold tw-mr-2">Aangemaakt</span>
        <span>{{ formatDate(caseDetail.created_at) }}</span>
      </div>
      <div class="tw-flex tw-mt-2" v-if="caseDetail.opened_at">
        <span class="tw-font-bold tw-mr-2">Geopend</span>
        <span>{{ formatDate(caseDetail.opened_at) }}</span>
      </div>
      <div class="tw-flex tw-mt-2">
        <span class="tw-font-bold tw-mr-2">Aangemaakt door</span>
        <span>{{ removeDomainFromEmail(caseDetail.created_by) }}</span>
      </div>
      <div class="tw-flex tw-mt-2" v-if="caseDetail.muted_till">
        <span class="tw-font-bold tw-mr-2">Gemute tot</span>
        <span>{{ formatDate(caseDetail.muted_till) }}</span>
      </div>

      <div class="tw-flex tw-my-3 tw-w-full tw-gap-3">
        <button
          v-show="caseActionAvailable('open', caseDetail.status)"
          class="tw-bg-blue-500 hover:tw-bg-blue-400 tw-rounded tw-p-1 tw-text-white tw-w-1/2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          @click="showConfirmation('open')"
        >
          Open
        </button>

        <button
          v-show="canBeClosed"
          class="tw-bg-red-500 hover:tw-bg-red-400 tw-rounded tw-p-1 tw-text-white tw-w-1/2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          @click="showConfirmation('cancel')"
        >
          Cancel
        </button>

        <button
          v-show="caseActionAvailable('mute', caseDetail.status)"
          class="tw-bg-gray-500 hover:tw-bg-gray-400 tw-rounded tw-p-1 tw-text-white tw-w-1/2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          @click="showConfirmation('mute')"
        >
          Mute
        </button>

        <button
          v-show="canBeClosed"
          class="tw-bg-green-500 hover:tw-bg-green-400 tw-rounded tw-p-1 tw-text-white tw-w-1/2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          @click="showConfirmation('finish')"
        >
          Finish
        </button>
      </div>
    </div>

    <generic-modal
      v-if="confirm.action"
      @close="confirm.action = null"
      @submit="confirmAction"
      :title="confirm.title"
    >
      <div>
        {{ caseDetail.address }}
      </div>
      <div v-if="confirm.action === 'updateCategory'">
        <FormField
          label="Categorie"
          nullOption="Alle categorieën"
          :options="categories"
          type="select"
          mode="tags"
          v-model="confirm.category"
          class="tw-w-[250px] tw-mr-5"
        />
      </div>
      <div v-if="confirm.action === 'mute'">
        <FormField
          label="Periode"
          type="select"
          v-model="confirm.period"
          :options="MUTE_PERIODS"
        />
      </div>
    </generic-modal>

    <div v-if="isLoading">Loading...</div>
  </div>
</template>

<script setup>
import { formatDate, notifications } from '@/util'
import { computed, reactive } from 'vue'
import { useMutation, useQueryClient, useQuery } from '@tanstack/vue-query'
import GenericModal from '@/components/shared/GenericModal.vue'
import {
  caseActionAvailable,
  caseActionPayload,
  hasBlockingTicket,
  MUTE_PERIODS,
} from '@/logic/common/CaseActions'
import { CASE_CATEGORIES } from '@/constants'
import { fetchResponse } from '@/util/fetch'
import FormField from '@/components/shared/FormField.vue'
import fetchData from '@/util/fetch'
import AlarmStatus from './AlarmStatus.vue'
import CaseStatus from './CaseStatus.vue'
import MonitoringLinks from '@/components/shared/MonitoringLinks.vue'

const queryClient = useQueryClient()

const categories = CASE_CATEGORIES

const confirm = reactive({
  title: '',
  action: null,
  period: '1 week',
  category: '',
})

const props = defineProps({
  caseDetail: {
    type: Object,
    required: true,
  },
})

const { mutate: updateCase, isLoading } = useMutation({
  mutationFn: async (payload) => {
    const response = await fetchResponse(`/tickets/api/v1/cases/${props.caseDetail.id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      notifications.addNotification({
        message: `Something went wrong: ${response.statusText}`,
        type: 'danger',
      })
      throw new Error(`Failed to update case: ${response.statusText}`)
    }

    const updatedCase = await response.json()
    notifications.addNotification({
      message: `Case ${props.caseDetail.id} updated successfully`,
      type: 'success',
    })
    queryClient.invalidateQueries(['cases'])
    queryClient.invalidateQueries(['caseDetail', String(props.caseDetail.id)])
    return updatedCase
  },
})

const showConfirmation = (action) => {
  confirm.action = action
  if (action === 'mute') {
    confirm.title = 'Voor hoe lang wil je deze case muten?'
  } else if (action === 'updateCategory') {
    confirm.title = 'Update the category'
    confirm.category = props.caseDetail.category
  } else {
    confirm.title = `Are you sure you want to ${action} this case?`
  }
  confirm.period = '1 week'
}

const confirmAction = async () => {
  if (confirm.action === 'updateCategory') {
    updateCase({ category: confirm.category })
  } else {
    updateCase(caseActionPayload(confirm.action, props.caseDetail.status, confirm.period))
  }

  confirm.action = null
}

const { data: ticketsQuery } = useQuery({
  queryKey: computed(() => ['ticketsQuery', props.caseDetail.id]),
  keepPreviousData: true,
  queryFn: async () => {
    try {
      const result = await fetchData(
        `/tickets/api/v1/tickets?view=list&case_id=${props.caseDetail.id}`
      )
      return result.data
    } catch (error) {
      console.error('Error fetching tickets:', error)
      throw error
    }
  },
})

const canBeClosed = computed(
  () =>
    caseActionAvailable('finish', props.caseDetail.status) &&
    !hasBlockingTicket(ticketsQuery.value || [])
)

function removeDomainFromEmail(email) {
  return email.replace('@ecorushome.com', '')
}
</script>
